/**
 * The weather go/no-go.
 *
 * The operator enters what they can see — a wind, a gust, a temperature, the
 * heading they will land on — and the panel returns the decision, not the raw
 * numbers: the crosswind on that heading, the headwind home, and a verdict
 * against the airframe's limits. There is no feed behind it; the honesty is
 * that it computes consequences from observed inputs rather than inventing a
 * forecast it does not have.
 */

import { useCallback, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { assessWeather, type WeatherAssessment, type WeatherVerdict } from '@/api/weather';
import { fetchCurrentWeather } from '@/api/openMeteo';
import { aorById } from '@/map/aors';
import { useDroneStore, usePrimarySelectedId } from '@/stores/droneStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';

const VERDICT_TONE: Record<WeatherVerdict, string> = {
  ok: 'var(--ok)',
  caution: 'var(--warn)',
  'no-go': 'var(--danger-foreground)',
};

const VERDICT_LABEL: Record<WeatherVerdict, string> = {
  ok: '● GO',
  caution: '● CAUTION',
  'no-go': '● NO-GO',
};

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputMode="decimal"
        placeholder={placeholder}
        className="well w-full min-w-0 rounded-[7px] px-2 py-1 font-mono text-[11px] outline-none"
      />
    </label>
  );
}

export function WeatherTab() {
  const [windSpeed, setWindSpeed] = useState('5');
  const [windFrom, setWindFrom] = useState('0');
  const [heading, setHeading] = useState('0');
  const [gust, setGust] = useState('');
  const [temp, setTemp] = useState('15');
  const [elevation, setElevation] = useState('0');
  const [result, setResult] = useState<WeatherAssessment | null>(null);
  // Two failures, two slots, deliberately not one. The weather feed being
  // unreachable and the assessment being unable to run are different problems
  // with different fixes, and neither one succeeding says anything about the
  // other. Sharing a slot meant the assessment — which reruns on a 250 ms
  // debounce whenever a field changes — cleared the fetch error a moment after
  // it appeared, so the operator was told the feed was down only if they
  // happened to be looking.
  const [error, setError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  // The provenance of the wind/gust/temp fields: null once the operator has
  // touched any of them, a labelled Open-Meteo timestamp right after a fetch.
  const [source, setSource] = useState<string | null>(null);

  const aor = useWorkspaceStore((state) => state.aor);
  const primaryId = usePrimarySelectedId();
  // The selected aircraft's ground position, if it has a fix. useShallow so a
  // telemetry-rate store update that leaves lat/lon unchanged does not re-render
  // this panel. Falls back to the AOR centre when nothing is selected.
  const dronePosition = useDroneStore(
    useShallow((state) => {
      const scalars = primaryId ? state.drones.get(primaryId)?.scalars : undefined;
      const lat = scalars?.lat;
      const lon = scalars?.lon;
      if (lat === undefined || lon === undefined || (lat === 0 && lon === 0)) {
        return null;
      }
      return { lat, lon };
    }),
  );

  // Editing a fetched field makes it operator-entered again, so the label never
  // claims Open-Meteo for a number a human has since overridden.
  const edited = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setSource(null);
  };

  const fetchAuto = useCallback(async () => {
    const position =
      dronePosition ??
      (() => {
        const [lon, lat] = aorById(aor).center;
        return { lat, lon };
      })();

    setFetching(true);
    const outcome = await fetchCurrentWeather(position.lat, position.lon);
    setFetching(false);

    if (!outcome.ok) {
      setFetchError(outcome.error);
      return;
    }

    const conditions = outcome.data;
    setFetchError(null);
    setWindSpeed(conditions.windSpeedMs.toFixed(1));
    setWindFrom(Math.round(conditions.windFromDeg).toString());
    setGust(conditions.gustMs > 0 ? conditions.gustMs.toFixed(1) : '');
    setTemp(conditions.temperatureC.toFixed(1));
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setSource(`Open-Meteo, ${time}`);
  }, [dronePosition, aor]);

  const run = useCallback(async () => {
    const num = (value: string, fallback: number) =>
      value.trim() === '' ? fallback : Number(value);
    const outcome = await assessWeather({
      windSpeedMs: num(windSpeed, 0),
      windFromDeg: num(windFrom, 0),
      headingDeg: num(heading, 0),
      gustMs: gust.trim() === '' ? undefined : Number(gust),
      temperatureC: num(temp, 15),
      fieldElevationM: num(elevation, 0),
    });
    if (outcome.ok) {
      setResult(outcome.data);
      setError(null);
    } else {
      setResult(null);
      setError(outcome.error.message);
    }
  }, [windSpeed, windFrom, heading, gust, temp, elevation]);

  // Recompute as the operator adjusts, on a short debounce so a live drag of
  // the wind reads smoothly rather than firing a request per keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => void run(), 250);
    return () => window.clearTimeout(id);
  }, [run]);

  return (
    <div className="flex flex-col gap-2">
      <div className="well flex flex-col gap-2 rounded-[11px] px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
            OBSERVED CONDITIONS
          </span>
          <button
            type="button"
            onClick={() => void fetchAuto()}
            disabled={fetching}
            className="ml-auto rounded-[99px] bg-white/10 px-2 py-0.5 label-mono text-[8px] text-[color:var(--foreground)] transition-colors hover:bg-white/20 disabled:opacity-40"
          >
            {fetching ? 'FETCHING…' : 'FETCH (POSITION)'}
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          <Field label="WIND m/s" value={windSpeed} onChange={edited(setWindSpeed)} />
          <Field label="FROM °" value={windFrom} onChange={edited(setWindFrom)} />
          <Field label="GUST m/s" value={gust} onChange={edited(setGust)} placeholder="—" />
        </div>
        <div className="flex items-center gap-1.5">
          <Field label="LAND HDG °" value={heading} onChange={setHeading} />
          <Field label="TEMP °C" value={temp} onChange={edited(setTemp)} />
          <Field label="ELEV m" value={elevation} onChange={setElevation} />
        </div>
        {/* Where the wind/gust/temp came from — a fetched observation carries its
            source and time; anything a human typed reads as operator-entered. */}
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
          {source ? `SRC · ${source}` : 'SRC · Operator-entered'}
        </span>
      </div>

      {fetchError ? (
        <p className="text-[10px] leading-snug text-[color:var(--danger-foreground)]">
          {fetchError}
        </p>
      ) : null}

      {error ? (
        <p className="text-[10px] leading-snug text-[color:var(--danger-foreground)]">{error}</p>
      ) : null}

      {result ? (
        <div className="well flex flex-col gap-1.5 rounded-[11px] px-3 py-2.5">
          <div className="flex items-center gap-2">
            <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
              LAUNCH DECISION
            </span>
            <span
              className="label-mono ml-auto text-[9px]"
              style={{ color: VERDICT_TONE[result.verdict] }}
            >
              {VERDICT_LABEL[result.verdict]}
            </span>
          </div>
          <Row label="CROSSWIND" value={`${result.crosswindMs.toFixed(1)} m/s`} />
          <Row
            label={result.headwindMs >= 0 ? 'HEADWIND' : 'TAILWIND'}
            value={`${Math.abs(result.headwindMs).toFixed(1)} m/s`}
            tone={result.headwindMs < -1 ? 'var(--warn)' : undefined}
          />
          {result.gustFactorMs > 0 ? (
            <Row label="GUST SPREAD" value={`${result.gustFactorMs.toFixed(0)} m/s`} />
          ) : null}
          <Row label="DENSITY ALT" value={`${Math.round(result.densityAltitudeM)} m`} />
          <p
            className="text-[10px] leading-snug"
            style={{ color: VERDICT_TONE[result.verdict] }}
          >
            {result.summary}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex items-baseline gap-2 text-[11px]">
      <span className="text-[color:var(--muted-foreground-2)]">{label}</span>
      <span className="tnum ml-auto" style={tone ? { color: tone } : undefined}>
        {value}
      </span>
    </div>
  );
}
